import { ReactElement, useState, useEffect } from "react";
import {
  Step,
  ResponseStep,
  CustomStepAssets,
  CustomFieldConfig,
  CustomCollectionsStepAssets,
} from "@dreambigger/shared/src/types";
import resolveCustomStepTitles from "../../utils/resolveCustomStepTitles";
import compileTemplate from "../../utils/compileTemplate";
import entriesFromResponseFields from "../../utils/entriesFromResponseFields";
import extractCustomFields from "../../utils/extractCustomFields";
import shouldDisplayField from "../../steps/custom-collections/utils/shouldDisplayField";
import { CheckOutlined, FormOutlined, EditFilled } from "@ant-design/icons";

import { useRouter } from "next/router";
import {
  formatAsCurrency,
  formatAsNationalPhoneNum,
  hashString,
} from "../../utils/textFormatting";
import ResponseStepField from "../ResponseStepField/ResponseStepField";
import {
  capitalizeFirstLetter,
  FULLY_CAPITALIZED_WORDS,
} from "@dreambigger/shared/src/utils/textFormatting";
import dayjs from "dayjs";
import styles from "./StepReviewCard.module.scss";
import toPlainText from "@dreambigger/shared/src/utils/toPlainText";
export interface ReviewSummaryProps {
  step: Step;
  i: number;
  response: ResponseStep | null;
  keyFilterList: string[];
  isV2?: boolean;
}

const StepReviewCard = ({
  step,
  i,
  response,
  keyFilterList,
  isV2 = false,
}: ReviewSummaryProps) => {
  const [fieldsRendered, setFieldsRendered] = useState<ReactElement[]>([]);
  const router = useRouter();

  useEffect(() => {
    setFieldsRendered([]);
    if (!response) {
      return;
    }
    getFieldsToRender(i, step.type);
  }, [response]);

  // ----------------------------------- Helper Methods -------------------------------------------
  const formatTitle = (title: string) => {
    if (!title) {
      return "";
    }
    const words = title.split(/(?=[A-Z]|-)/);
    const formattedWords = words.map((word) => {
      // Replace every hyphen found with a space
      const formattedWord = word.replace(/-([a-z])/g, (_, letter) => letter);
      // If the word is one of the ones that should be fully capitalized, then do so.
      if (FULLY_CAPITALIZED_WORDS.includes(formattedWord.toLowerCase())) {
        return formattedWord.toUpperCase();
      }
      // Otherwise, capitalize the first letter of the word.
      return capitalizeFirstLetter(formattedWord);
    });
    return formattedWords.join(" ");
  };

  // * "Select" and "Autocomplete" fields are saved with the value of the option selected, not the label.
  //   This is not user-friendly, as the user saw the label when making a selection.
  //   This function replaces the value saved with the label of the option so that the user is not confused.
  const replaceValueWithLabel = (value: string) => {
    if (!response) return value;
    // Get the field associated with the provided value from the response.
    const fieldAssociatedWithValue = Object.keys(response.fields).find(
      (f) => response.fields[f] === value
    );

    // Find the custom field config associated with the field.
    const assets = step.assets as CustomStepAssets;
    const customField = assets?.customFields?.find(
      (f) => f.fieldKey === fieldAssociatedWithValue
    );

    // Use the config to find the field option associated with the provided value.
    const customOption =
      customField && "fieldOptions" in customField
        ? customField.fieldOptions?.find(
            (o) => typeof o === "object" && o.value === value
          )
        : undefined;

    // If a custom field or option is not found, return the value.
    if (!customField || !customOption) {
      if (typeof value === "string") {
        return formatString(value);
      }
      return value;
    }

    // Return the label for the saved value
    if (typeof customOption === "object") {
      return (
        customOption.label ||
        ("text" in customOption ? customOption.text : undefined)
      );
    }
  };

  const formatString = (answer: string | number) => {
    const answerString = answer.toString();
    if (
      answerString &&
      FULLY_CAPITALIZED_WORDS.includes(answerString.toLowerCase())
    ) {
      return answerString.toUpperCase();
    }
    return capitalizeFirstLetter(answerString);
  };

  const formatAnswer = (answer: any, fieldType: string) => {
    if (answer === true || answer.toString().toLowerCase() === "true") {
      return <CheckOutlined />;
    }
    switch (fieldType) {
      case "phone":
        return formatAsNationalPhoneNum(answer);
      case "ssn":
        return hashString(answer, 4);
      case "date":
        return dayjs(answer).format("MM/DD/YYYY");
      case "dollarInput":
      case "currency":
        return formatAsCurrency(parseFloat(answer));
      case "percent":
        return capitalizeFirstLetter(answer) + "%";
      case "select":
      case "autocomplete":
        return replaceValueWithLabel(answer);
      // Emails render exactly as entered. The `default` branch capitalizes the
      // first letter, which turns "name@example.com" into "Name@example.com"
      // on the review screen.
      case "email":
        return answer;
      default:
        if (Array.isArray(answer)) {
          return answer.map((a: string) => formatString(a)).join(", ");
        }
        return formatString(answer);
    }
  };

  // ------------------------------------------- Render Methods --------------------------------------------

  const renderCustomFields = () => {
    if (!response) return;
    // Define an empty fields array to push fields into for rendering.
    const fields: any = [];
    // CUSTOM STEP
    const customAssets: CustomStepAssets = step.assets;
    const rFields = response.fields;
    const allowRepeats = customAssets.allowRepeats || false;

    // If allowRepeats, there could be multiple answers per step with a suffix such as __0, __1, etc.
    if (allowRepeats) {
      customAssets?.customFields?.map((field: CustomFieldConfig) => {
        // Disclosure fields surface in the dedicated AttestationSummary block,
        // not the per-step review rows.
        if (field.fieldType === "disclosure") return;
        const fKey = field.fieldKey;
        const baseFieldKey = fKey.replace(/__\d+$/, "");

        // Find the highest suffix number to determine how many times the field was answered.
        let repeatCount = 0;
        Object.keys(rFields).forEach((key) => {
          if (key.startsWith(baseFieldKey + "__")) {
            const suffixNum = parseInt(key.slice(baseFieldKey.length + 2), 10);
            if (!isNaN(suffixNum) && suffixNum > repeatCount) {
              repeatCount = suffixNum;
            }
          }
        });

        // Loop through all possible suffixes and render the field if it exists.
        for (let i = 0; i <= repeatCount; i++) {
          const fKeyWithSuffix = baseFieldKey + "__" + i;
          // If the field doesn't exist, is false, or is in the keyFilterList, skip it.
          if (
            !rFields[fKeyWithSuffix] ||
            rFields[fKeyWithSuffix].toString().toLowerCase() === "false" ||
            keyFilterList.includes(fKeyWithSuffix)
          ) {
            continue;
          }

          // If the field is a rate, format it as a percent.
          const fieldType = fKey.includes("rate") ? "percent" : field.fieldType;
          const formattedKey = `(${i + 1}) ${formatTitle(fKey)}`;
          const formattedValue = formatAnswer(
            rFields[fKeyWithSuffix],
            fieldType
          );
          fields.push({
            key: formattedKey,
            value: formattedValue,
          });
        }
      });
    } else {
      // If allowRepeats is false, there will only be one answer per field.
      // Follows a similar logic to the above, but without the suffixes.
      customAssets?.customFields?.map((field: CustomFieldConfig) => {
        // Disclosure fields surface in the dedicated AttestationSummary block,
        // not the per-step review rows.
        if (field.fieldType === "disclosure") return;
        const fKey = field.fieldKey;
        if (
          !rFields[fKey] ||
          rFields[fKey].toString().toLowerCase() === "false" ||
          keyFilterList.includes(fKey)
        ) {
          return;
        }
        const fieldType = fKey.includes("rate") ? "percent" : field.fieldType;
        const formattedKey = formatTitle(fKey);
        const formattedValue = formatAnswer(rFields[fKey], fieldType);
        fields.push({
          key: formattedKey,
          value: formattedValue,
        });
      });
    }
    const renderedKeys: string[] = [];
    const renderedFields: any = fields.map((field: any, index: number) => {
      // If there are duplicates in the field config (creative measures for condtionaly displaying fields), skip rendering subsequent fields with the same key.
      if (renderedKeys.includes(field.key)) {
        return;
      }
      renderedKeys.push(field.key);
      return (
        <ResponseStepField
          key={field.key}
          title={field.key}
          value={field.value}
          applyBackground={!isV2 && index % 2 === 0}
          isV2={isV2}
        />
      );
    });
    // Set the fields to be rendered in state and return.
    setFieldsRendered(renderedFields);
  };

  const renderTruStageFields = () => {
    if (!response) return;
    // Define an empty fields array to push fields into for rendering.
    const fields: any = [];

    Object.entries(response.fields).forEach(([key, answer]) => {
      const title =
        key === "CI"
          ? "Credit Insurance"
          : key === "DP"
          ? "Debt Protection"
          : key;
      const value = answer === true ? "Interested" : "Not Interested";
      fields.push(
        <ResponseStepField
          title={title}
          value={value}
          applyBackground={!isV2 && fields.length % 2 === 0}
          isV2={isV2}
        />
      );
    });

    setFieldsRendered(fields);
  };

  const renderCustomCollectionsFields = () => {
    if (!response) return;
    const assets = step.assets as CustomCollectionsStepAssets;
    const entries = entriesFromResponseFields(response.fields);
    if (entries.length === 0) return;

    const displayConfig = resolveCustomStepTitles(assets);
    const customFields = extractCustomFields(assets.customFields);
    const fields: ReactElement[] = [];
    // Entry headings distinguish multiple records. On single-entry responses
    // the heading adds no information, so skip it.
    const showEntryHeadings = entries.length > 1;
    let fieldIndex = 0;

    entries.forEach((entry) => {
      if (showEntryHeadings) {
        const title =
          compileTemplate(displayConfig.entryHeader.title, entry) ||
          `Entry ${fields.length + 1}`;
        fields.push(
          <ResponseStepField
            key={`entry-heading-${entry.id}`}
            title=""
            value={<strong>{title}</strong>}
            applyBackground={false}
            isV2={isV2}
          />
        );
      }

      customFields.forEach((field) => {
        // Mirror the form-side display-condition filtering so fields hidden
        // during entry don't reappear on the review page.
        if (
          !shouldDisplayField(field, entry, undefined, undefined, customFields)
        ) {
          return;
        }

        const value = entry[field.fieldKey];
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          value.toString().toLowerCase() === "false"
        ) {
          return;
        }

        const fieldType = field.fieldKey.includes("rate")
          ? "percent"
          : field.fieldType;
        // ResponseStepField renders the title as a text node while the form
        // itself renders `fieldLabel` through dangerouslySetInnerHTML, so any
        // label authored with markup — which the rich text control makes the
        // norm, not the exception — printed its tags here.
        const label =
          toPlainText(field.fieldLabel) || formatTitle(field.fieldKey);

        fields.push(
          <ResponseStepField
            key={`${entry.id}-${field.fieldKey}`}
            title={label}
            value={formatAnswer(value, fieldType)}
            applyBackground={!isV2 && fieldIndex % 2 === 0}
            isV2={isV2}
          />
        );
        fieldIndex++;
      });
    });

    setFieldsRendered(fields);
  };

  const getFieldsToRender = (i: number, stepType: string) => {
    if (!response) return;
    const fields: any = [];
    let fieldOrder: { key: string; type: string }[] = [];
    switch (stepType) {
      // If a custom field, follow special rendering logic.
      case "custom": {
        const variant = (step.assets as { variant?: string })?.variant;
        if (variant === "collection") {
          renderCustomCollectionsFields();
        } else {
          renderCustomFields();
        }
        return;
      }
      case "truStage":
        renderTruStageFields();
        return;
      // For all other step types, render the fields in the order defined in the switch statement.
      // If the fieldOrder is empty, nothing will be rendered.
      case "start":
        fieldOrder = [
          { key: "applicationId", type: "text" },
          { key: "email", type: "email" },
          { key: "phone", type: "phone" },
        ];
        break;
      case "name":
        fieldOrder = [
          { key: "fullName", type: "text" },
          { key: "terms", type: "checkbox" },
        ];
        break;
      case "address":
        fieldOrder = [
          { key: "address", type: "text" },
          { key: "apartment", type: "text" },
          { key: "city", type: "text" },
          { key: "state", type: "text" },
          { key: "zip", type: "text" },
        ];
        break;
      case "ssn":
        fieldOrder = [
          { key: "ssn", type: "ssn" },
          { key: "birthday", type: "date" },
          { key: "identityConfirmation", type: "checkbox" },
        ];
        break;
      case "additional-contact":
        fieldOrder = [
          { key: "email", type: "email" },
          { key: "phone", type: "phone" },
        ];
        break;
      case "idv-vouched":
        fieldOrder = [{ key: "vouchedVerificationSuccess", type: "checkbox" }];
        break;
      case "docusign":
        fieldOrder = [{ key: "status", type: "text" }];
        break;
      case "loan-rate":
        fieldOrder = [
          { key: "loanAmount", type: "currency" },
          { key: "termInMonths", type: "text" },
          { key: "rate", type: "percent" },
          { key: "monthlyPayment", type: "currency" },
        ];
        break;
      case "cd-interest":
        fieldOrder = [{ key: "principal", type: "currency" }];
        break;
      case "existing-core-accounts":
        fieldOrder = [{ key: "accountNumber", type: "text" }];
      /// Cases where we don't want anything to render. The IDV steps store a
      /// session uuid and an outcome, neither of which means anything to an
      /// applicant reviewing their answers.
      // eslint-disable-next-line no-fallthrough
      case "plaid":
      case "plaid-auth":
      case "plaid-idv":
      case "socure-idv":
      case "plaid-income":
      case "people":
      case "payment":
      case "payment-status":
      case "review":
      case "success":
        break;
      case "initial-deposit":
        fieldOrder = Object.keys(response.fields).map((key) => ({
          key,
          type: "currency",
        }));
        break;
      default:
        fieldOrder = Object.keys(response.fields).map((key) => ({
          key,
          type: "text",
        }));
        break;
    }

    fieldOrder.forEach((field) => {
      const { key, type } = field;
      if (key in response.fields && !keyFilterList.includes(key)) {
        let value = response.fields[key];
        value &&
          fields.push(
            <ResponseStepField
              title={formatTitle(key)}
              value={formatAnswer(value, type)}
              applyBackground={!isV2 && fields.length % 2 === 0}
              isV2={isV2}
            />
          );
      }
    });

    setFieldsRendered(fields);
  };

  // ------------------------------------------- Final Render ----------------------------------------------

  // If there are no fields to render, return null. This prevents a step without useful data from rendering,
  // such as an optional step where every prompt is skipped.
  if (fieldsRendered.length === 0) {
    return null;
  }

  if (isV2) {
    return (
      <div className={styles.v2ReviewCard}>
        {/* LEFT COLUMN - Displays a sequence of checkmarks connected by lines vertically for each step */}
        <div className={styles.leftColumn}>
          {/* Vertical line */}
          <div className={`${styles.topLine} ${i > 0 ? "h-2" : "mt-2"}`} />
          {/* Circle with checkmark */}
          <div
            style={{
              width: "24px",
              height: "24px",
              minWidth: "24px",
              minHeight: "24px",
            }}
            className={`br-100 flex justify-center items-center textPrimary bg-primary`}
          >
            <CheckOutlined style={{ fontSize: "14px" }} />
          </div>
          {/* Vertical line - always rendered, hidden via CSS on last child */}
          <div className={`${styles.bottomLine} h-100`} />
        </div>
        {/* RIGHT COLUMN - Wrapper that contains both title and answers to maintain height */}
        <div className={`${styles.rightColumn}  bg-gray-4 br-5 ph-4 pt-1 pb-4`}>
          {/* Title and Edit Icon - Outside the white box but inside height wrapper */}
          <div className="flex justify-between items-center mb-2">
            <h2 className="fwt f-6" style={{ color: "#323333", margin: 0 }}>
              {formatTitle(step.slug)}
            </h2>
            {/* Edit Icon - Takes user back to corresponding step. Disabled for start step. */}
            {i > 0 && (
              <EditFilled
                className="pointer hlight-primary"
                style={{ color: "#666", fontSize: "18px" }}
                onClick={() => {
                  router.push({
                    query: {
                      ...router.query,
                      stepSlug: response?.slug,
                    },
                  });
                }}
              />
            )}
          </div>
          {/* White box with answers */}
          <div className="flex flex-column bg-white br-4 w-100">
            {/* Render fields - These are defined by getFieldsToRender() */}
            {fieldsRendered}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      {/* LEFT COLUMN - Displays a sequence of checkmarks connected by lines vertically for each step */}
      <div
        className={`flex flex-column items-center mr-2`}
        style={{ width: "16px" }}
      >
        {/* Vertical line */}
        <div
          className={`bl ${i > 0 ? `h-2` : `mt-2`}`}
          style={{ width: "1px", color: "lightgray" }}
        />
        {/* Circle with checkmark */}
        <div
          style={{ width: "12px", height: "12px" }}
          className={`br-100 pa-2 s-2 flex justify-center items-center textPrimary bg-primary`}
        >
          <CheckOutlined style={{ fontSize: "10px" }} />
        </div>
        {/* Vertical line */}
        <div
          className={`bl h-100 `}
          style={{ width: "1px", color: "lightgray" }}
        />
      </div>
      {/* RIGHT COLUMN- Displays a card that is filled with fields */}
      <div
        className="flex flex-column bg-white br-2 s-2 w-100 mb-2"
        style={{ overflow: "hidden" }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <h2 className="fwt mb-2 pl-2" style={{ color: "#323333" }}>
              {formatTitle(step.slug)}
            </h2>
          </div>
          {/* Edit Icon - Takes user back to corresponding step. Disabled for start step. */}
          {i > 0 && (
            <FormOutlined
              className="mr-2 pointer primary"
              onClick={() => {
                router.push({
                  query: {
                    ...router.query,
                    stepSlug: response?.slug,
                  },
                });
              }}
            />
          )}
        </div>
        {/* Render fields - These are defined by getFieldsToRender() */}
        {fieldsRendered}
      </div>
    </div>
  );
};
export default StepReviewCard;
